import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import tenantService from '../services/tenantService';
import superAdminService from '../services/superAdminService'; // Import superAdminService
import { getMockTenant, getValidTenantId } from '../utils/tenantUtils';
import { handleTenantFetchError } from '../utils/tenantErrorHandling';

export const TenantContext = createContext();

export const useTenant = () => useContext(TenantContext);

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});
  const [previousTenantId, setPreviousTenantId] = useState(null);

  // Load tenant data
  const loadTenant = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Save the previous tenant ID for comparison
      const currentTenantId = localStorage.getItem('tenantId');
      
      // Only update previousTenantId if it's different to avoid re-renders
      if (currentTenantId && currentTenantId !== previousTenantId) {
        setPreviousTenantId(currentTenantId);
      }
      
      // Initialize tenant context first
      const debugInfo = await tenantService.initTenantContext();
      setDebugInfo(debugInfo);
      
      // Always try to get the latest tenant data from server first
      try {
        const tenantId = getValidTenantId({ useLocalStorage: true });
        if (tenantId) {
          console.log('Attempting to fetch tenant data from server with ID:', tenantId);
          
          // Use different method based on user role to prevent 403 errors
          let response;
          const isAdmin = localStorage.getItem('isAdmin') === 'true';
          const isSuperAdmin = localStorage.getItem('isSuperAdmin') === 'true';
          
          if (isSuperAdmin) {
            // SuperAdmin uses the superadmin endpoint
            response = await superAdminService.getTenantById(tenantId);
          } else {
            // Regular admins and members use the tenant endpoint
            response = await tenantService.getCurrentTenantDetails();
          }
          
          if (response?.data) {
            const processedTenant = validateAndFixTenantData(response.data);
            setTenant(processedTenant);
            // Store the updated data to localStorage for persistence through logout
            tenantService.setCurrentTenant(processedTenant);
            console.log('Successfully retrieved tenant data from server');
            setIsInitialized(true);
            setLoading(false);
            return processedTenant;
          }
        }
      } catch (serverError) {
        console.warn('Failed to fetch tenant from server, falling back to local:', serverError);
      }
      
      // Then fetch current tenant details as fallback
      const response = await tenantService.fetchCurrentTenant();
      
      if (response) {
        // Process tenant data to ensure it has all required fields
        const tenantData = response.data || response;
        const processedTenant = validateAndFixTenantData(tenantData);
        setTenant(processedTenant);
        
        // Check if tenant has changed
        const newTenantId = processedTenant?._id || processedTenant?.id;
        if (newTenantId && newTenantId !== previousTenantId) {
          console.log(`TenantContext: Tenant changed from ${previousTenantId} to ${newTenantId}`);
          
          // Dispatch a custom event to notify about tenant change
          const tenantChangeEvent = new CustomEvent('tenantChanged', {
            detail: { 
              tenantId: newTenantId,
              previousTenantId: previousTenantId
            }
          });
          window.dispatchEvent(tenantChangeEvent);
        }
      } else {
        // Use fallback local data if API doesn't return anything
        const localTenant = tenantService.getCurrentTenant();
        if (localTenant) {
          console.log('Using local tenant data:', localTenant);
          const processedTenant = validateAndFixTenantData(localTenant);
          setTenant(processedTenant);
        } else {
          console.warn('No tenant data available');
          setError('No tenant data available');
        }
      }
      
      setIsInitialized(true);
    } catch (err) {
      console.error('Error loading tenant data:', err);
      setError(err.message || 'Failed to load tenant data');
      
      // Use fallback to local storage as a last resort
      try {
        const localTenant = tenantService.getCurrentTenant();
        if (localTenant) {
          console.log('Error occurred, falling back to local tenant data');
          const processedTenant = validateAndFixTenantData(localTenant);
          setTenant(processedTenant);
        }
      } catch (localError) {
        console.error('Error loading from local storage:', localError);
      }
    } finally {
      setLoading(false);
    }
  }, [previousTenantId]);

  // Load tenant on first mount
  useEffect(() => {
    if (!isInitialized) {
      loadTenant();
    }
  }, [loadTenant, isInitialized]);

  // Deep validation and structure fixing for tenant data
  const validateAndFixTenantData = (tenantData) => {
    if (!tenantData) return null;
    
    // Create a clone of tenant data to avoid mutating the original
    const validatedTenant = JSON.parse(JSON.stringify(tenantData));
    
    // Make sure the tenant has an ID
    if (!validatedTenant._id && validatedTenant.id) {
      validatedTenant._id = validatedTenant.id;
    } else if (!validatedTenant._id && !validatedTenant.id) {
      const tenantId = localStorage.getItem('tenantId');
      if (tenantId) {
        validatedTenant._id = tenantId;
      }
    }
    
    // Ensure settings object exists
    if (!validatedTenant.settings) {
      validatedTenant.settings = {};
    }
    
    // Ensure adminEmail exists - silently set a default instead of warning
    if (!validatedTenant.adminEmail) {
      // Try to find an admin email in other properties
      if (validatedTenant.owner && validatedTenant.owner.email) {
        validatedTenant.adminEmail = validatedTenant.owner.email;
      } else if (validatedTenant.admin && validatedTenant.admin.email) {
        validatedTenant.adminEmail = validatedTenant.admin.email;
      } else {
        // Set a default admin email to prevent errors and warnings
        validatedTenant.adminEmail = 'admin@' + (validatedTenant.subdomain || 'example') + '.com';
      }
    }
    
    // Store the tenant in localStorage for persistence
    if (validatedTenant) {
      localStorage.setItem('currentTenant', JSON.stringify(validatedTenant));
      
      if (validatedTenant._id) {
        const previousTenantId = localStorage.getItem('tenantId');
        
        const newTenantId = validatedTenant._id;
        
        // Check if the tenant ID is changing
        if (previousTenantId !== newTenantId) {
          console.log(`TenantContext: Setting tenant ID from ${previousTenantId} to ${newTenantId}`);
          localStorage.setItem('tenantId', newTenantId);
          
          // Set a data attribute on the document to help with CSS isolation
          document.documentElement.setAttribute('data-tenant-id', newTenantId);
        }
      }
    }
    
    return validatedTenant;
  };

  // Update tenant settings with optimistic UI update
  const updateTenantSettings = useCallback(async (tenantId, settingsData) => {
    if (!tenantId) {
      console.error('TenantContext: Cannot update settings - missing tenant ID');
      return { status: 400, error: 'Missing tenant ID' };
    }
    
    // Security check - ensure we're updating the correct tenant
    const currentTenantId = localStorage.getItem('tenantId');
    if (tenantId !== currentTenantId) {
      console.error(`TenantContext: Tenant ID mismatch - attempted to update ${tenantId} while current tenant is ${currentTenantId}`);
      return { status: 403, error: 'Tenant ID mismatch' };
    }
    
    // Optimistically update local tenant state
    if (tenant) {
      const updatedTenant = structuredClone(tenant); // Use structuredClone for deep copy
      
      if (settingsData.name) updatedTenant.name = settingsData.name;
      if (settingsData.logo) updatedTenant.logo = settingsData.logo;
      if (settingsData.primaryColor) updatedTenant.primaryColor = settingsData.primaryColor;
      if (settingsData.adminEmail) updatedTenant.adminEmail = settingsData.adminEmail;
      
      // Handle settings updates with deep merge
      if (settingsData.settings) {
        updatedTenant.settings = updatedTenant.settings || {};
        
        Object.keys(settingsData.settings).forEach(key => {
          if (typeof settingsData.settings[key] === 'object' && settingsData.settings[key] !== null && !Array.isArray(settingsData.settings[key])) {
            updatedTenant.settings[key] = {
              ...(updatedTenant.settings[key] || {}),
              ...settingsData.settings[key]
            };
          } else {
            updatedTenant.settings[key] = settingsData.settings[key];
          }
        });
      }
      
      // Handle payment settings updates
      if (settingsData.paymentSettings) {
        updatedTenant.paymentSettings = {
          ...(updatedTenant.paymentSettings || {}),
          ...settingsData.paymentSettings
        };
      }
      
      // Update state immediately for responsive UI
      setTenant(updatedTenant);
      
      // Update localStorage to keep things in sync
      localStorage.setItem('currentTenant', JSON.stringify(updatedTenant));
      
      // Then make API call
      try {
        const result = await tenantService.updateTenantSettings(tenantId, settingsData);
        
        // Force a refresh of tenant data from server after successful update
        if (!result.localOnly) {
          try {
            setTimeout(async () => {
              await loadTenant(true); // Force refresh tenant data
            }, 300); // Small delay to ensure server has processed the update
          } catch (refreshError) {
            console.warn('Error refreshing tenant data after settings update:', refreshError);
          }
        }
        
        // If there was a server error but we updated locally
        if (result.localOnly) {
          console.log('Settings updated locally only due to server error');
          return { ...result, tenant: updatedTenant };
        }
        
        // If server update was successful, refresh tenant data to ensure consistency
        if (result.data?.tenant) {
          setTenant(result.data.tenant);
        } else if (result.data?.data) {
          setTenant(result.data.data);
        }
        
        return result;
      } catch (error) {
        // Even if API fails, we already updated the local state
        console.error('Failed to update tenant settings on server:', error);
        return {
          status: 200,
          data: { success: true, localOnly: true },
          localOnly: true,
          error
        };
      }
    } else {
      throw new Error('No tenant context available');
    }
  }, [tenant, loadTenant]);

  // Manually set the current tenant (useful when tenant info comes from login)
  const setCurrentTenant = useCallback((tenantData) => {
    if (!tenantData) return;
    
    // Save current tenant ID for comparison
    const previousId = localStorage.getItem('tenantId');
    
    // Process the tenant data
    const processedTenant = validateAndFixTenantData(tenantData);
    setTenant(processedTenant);
    
    // Store in localStorage for persistence
    localStorage.setItem('currentTenant', JSON.stringify(processedTenant));
    
    const newId = processedTenant._id || processedTenant.id;
    if (newId) {
      // Check if tenant is changing
      if (previousId !== newId) {
        localStorage.setItem('tenantId', newId);
        console.log(`TenantContext: Tenant changed from ${previousId} to ${newId}`);
        
        // Dispatch tenant change event
        const tenantChangeEvent = new CustomEvent('tenantChanged', {
          detail: { 
            tenantId: newId,
            previousTenantId: previousId
          }
        });
        window.dispatchEvent(tenantChangeEvent);
        
        // Update the document data attribute for CSS isolation
        document.documentElement.setAttribute('data-tenant-id', newId);
      }
    }
  }, []);

  return (
    <TenantContext.Provider 
      value={{ 
        tenant, 
        loading, 
        error,
        debugInfo,
        updateTenantSettings,
        setCurrentTenant,
        refreshTenant: async () => {
          return await loadTenant(true); // Pass true to force refresh
        },
        // Add helper method for tenant-scoped CSS class names
        getTenantScopedClass: (baseClassName) => {
          const tenantId = tenant?._id || tenant?.id;
          return tenantId ? `${baseClassName}-tenant-${tenantId}` : baseClassName;
        }
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};